import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from 'react';
import {
  Organization,
  OrganizationMember,
  UserOrganizationContext,
} from '@/types/organization';
import {
  getUserOrganizations,
  getUserRoleInOrganization,
  getOrganization,
  generateOrganizationCompanyContext,
  updateOrganizationCompany,
} from '@/services/organizationService';
import { updateUserData } from '@/services/userService';
import { useAuth } from './useAuthApi';

interface OrganizationContextProps extends UserOrganizationContext {
  switchOrganization: (organizationId: string) => Promise<void>;
  refreshOrganizations: () => Promise<void>;
  createOrganization: (name: string, companyUrl?: string) => Promise<Organization>;
  isAdmin: boolean;
  isMember: boolean;
  // Company-related functions
  updateCompanyContext: (companyData: any) => Promise<void>;
  generateCompanyContextFromUrl: (companyUrl: string) => Promise<void>;
  // Support and resource settings
  updateOrganizationSettings: (settings: { supportEmail?: string; platformResources?: any }) => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextProps | undefined>(undefined);

// Helper to transform organization data
const transformOrganization = (org: Organization | null): Organization | null => {
  // Company data is now directly in the company field
  return org;
};

// SessionStorage key for persisting selected organization
const SELECTED_ORG_KEY = 'hobbes-selected-organization';

export const OrganizationProvider = ({ children }: { children: ReactNode }) => {
  const { user, userData } = useAuth();
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [userRole, setUserRole] = useState<'admin' | 'member' | 'owner' | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch user's organizations
  const fetchOrganizations = async () => {
    if (!user) {
      setOrganizations([]);
      setCurrentOrganization(null);
      setUserRole(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const userOrgs = await getUserOrganizations(user.id);
      setOrganizations(userOrgs);

      // Set current organization based on sessionStorage, user preference, or first org
      if (userOrgs.length > 0) {
        const sessionOrgId = sessionStorage.getItem(SELECTED_ORG_KEY);
        const targetOrgId = sessionOrgId || userData?.currentOrganizationId || userOrgs[0].id;
        const targetOrg = userOrgs.find(org => org.id === targetOrgId) || userOrgs[0];
        
        // Fetch full org object to get company data
        const fullTargetOrg = await getOrganization(targetOrg.id);
        
        const transformed = transformOrganization(fullTargetOrg);
        setCurrentOrganization(transformed);

        // Sync the full org data (including logoUrl) into the organizations array
        if (transformed) {
          setOrganizations(prev => prev.map(o => o.id === transformed.id ? transformed : o));
        }

        // Save to sessionStorage for persistence across page refreshes
        sessionStorage.setItem(SELECTED_ORG_KEY, targetOrg.id);
        
        // Get user's role in the current organization
        const role = await getUserRoleInOrganization(user.id, targetOrg.id);
        setUserRole(role);
      } else {
        setCurrentOrganization(null);
        setUserRole(null);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setOrganizations([]);
      setCurrentOrganization(null);
      setUserRole(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Switch to a different organization
  const switchOrganization = async (organizationId: string) => {
    if (!user) return;

    try {
      const org = await getOrganization(organizationId);
      if (!org) {
        throw new Error('Organization not found');
      }

      setCurrentOrganization(transformOrganization(org));
      
      // Get user's role in the new organization
      const role = await getUserRoleInOrganization(user.id, organizationId);
      setUserRole(role);

      // Save to sessionStorage for persistence across page refreshes
      sessionStorage.setItem(SELECTED_ORG_KEY, organizationId);

      // Update user's currentOrganizationId in Firestore
      await updateUserData(user.id, {
        currentOrganizationId: organizationId
      });
    } catch (error) {
      console.error('Error switching organization:', error);
    }
  };

  // Create a new organization
  const createOrganization = async (name: string, companyUrl?: string): Promise<Organization> => {
    if (!user) {
      throw new Error('User must be authenticated to create organization');
    }

    try {
      // Import dynamically to avoid circular dependency issues
      const { createOrganization: createOrgService } = await import('../services/organizationService');
      const newOrg = await createOrgService(name, user.id, companyUrl);
      
      // Refresh organizations to include the new one
      await refreshOrganizations();
      
      // Switch to the new organization (pass directly to avoid lookup race condition)
      setCurrentOrganization(newOrg);
      setUserRole('admin'); // Creator is always admin
      
      return newOrg;
    } catch (error) {
      console.error('Error creating organization:', error);
      throw error;
    }
  };

  // Refresh organizations data
  const refreshOrganizations = async () => {
    await fetchOrganizations();
  };

  // Company-related functions
  const updateCompanyContext = async (companyData: any) => {
    if (!currentOrganization) {
      throw new Error('No current organization selected');
    }

    try {
      await updateOrganizationCompany(currentOrganization.id, companyData);
      
      // Refresh current organization to get updated company data
      const updatedOrg = await getOrganization(currentOrganization.id);
      if (updatedOrg) {
        setCurrentOrganization(transformOrganization(updatedOrg));
      }
    } catch (error) {
      console.error('Error updating company context:', error);
      throw error;
    }
  };

  const generateCompanyContextFromUrl = async (companyUrl: string) => {
    if (!currentOrganization) {
      throw new Error('No current organization selected');
    }

    try {
      await generateOrganizationCompanyContext(currentOrganization.id, companyUrl);
      
      // Refresh current organization to get updated company data
      const updatedOrg = await getOrganization(currentOrganization.id);
      if (updatedOrg) {
        setCurrentOrganization(transformOrganization(updatedOrg));
      }
    } catch (error) {
      console.error('Error generating company context:', error);
      throw error;
    }
  };

  const updateOrganizationSettings = async (settings: { supportEmail?: string; platformResources?: any }) => {
    if (!currentOrganization) {
      throw new Error('No current organization selected');
    }

    try {
      // Use dynamic import to avoid circular dependency
      const { updateOrganization } = await import('../services/organizationService');
      await updateOrganization(currentOrganization.id, settings);
      
      // Refresh current organization to get updated settings
      const updatedOrg = await getOrganization(currentOrganization.id);
      if (updatedOrg) {
        setCurrentOrganization(transformOrganization(updatedOrg));
      }
    } catch (error) {
      console.error('Error updating organization settings:', error);
      throw error;
    }
  };

  // Note: Automatic invitation acceptance has been disabled
  // Users must manually accept invitations via the invitation page

  // Load organizations when user changes - useCallback to prevent dependency issues
  const fetchOrganizationsCallback = useCallback(async () => {
    await fetchOrganizations();
  }, [user, userData?.currentOrganizationId]);

  useEffect(() => {
    fetchOrganizationsCallback();
  }, [fetchOrganizationsCallback]);

  const value: OrganizationContextProps = {
    currentOrganization,
    userRole,
    organizations,
    isLoading,
    switchOrganization,
    refreshOrganizations,
    createOrganization,
    isAdmin: userRole === 'admin' || userRole === 'owner',
    isMember: userRole === 'member' || userRole === 'admin',
    updateCompanyContext,
    generateCompanyContextFromUrl,
    updateOrganizationSettings,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};
